import { getOrCreateStore, GlobalState } from '@store';
import JSCookie from 'js-cookie';
import { NextComponentType } from 'next';
import { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import React, { useCallback, useEffect, useState } from 'react';
import Header from 'src/components/Header';
import { App } from 'src/types';

import { ThemeName, ThemeNames } from '@sravni/design-system-theme';
import { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { CommonHead } from '@sravni/next-common-head';
import { Banner, BANNER_TYPES } from '@sravni/react-advertisement';
import { Notifications } from '@sravni/react-design-system';
import { Footer } from '@sravni/react-footer';
import { DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import { ILocation } from '@sravni/types/lib/locations';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';
import { sentryInit } from '@sravni/ugc/lib/utils/sentry/sentryInit';
import { formatUserData } from '@sravni/utils/lib/auth';

import { OrganizationTypeToGatewayServiceMap } from '@constants/organizationType';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { TBannersList } from '@interfaces/advertising';
import { getBanners } from '@services/advertising';
import { setBanners } from '@store/reducers/advertising';
import { setCurrentLocation, setLocations } from '@store/reducers/locations';
import { setMetadata } from '@store/reducers/metadata';
import { setSettings } from '@store/reducers/sitesettings';
import { setFingerprint, setUser } from '@store/reducers/user';
import { getSiteSettings } from '@store/selectors/sitesettings';
import { getFingerprint, getUser } from '@store/selectors/users';
import { sendSpaIsReadyEvent } from '@utils/analytics';
import { isProdBuild, isProduction } from '@utils/environment';

import Seo from '../components/Seo';
import { useOrganizationType } from '../hooks/organizationType';
import { StoreProvider } from '../providers/store';
import styles from '../styles/page.module.scss';

import '@sravni/design-system-theme/lib/globalStyles.css';
import 'intersection-observer';

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
  locations: ILocation[];
}

export const AB_NAME = 'AB_RATINGS';

const MyApp: NextComponentType<App.ExtendedContext, AppInitialProps, IProps> = (props) => {
  const { Component, pageProps, initialReduxState, deviceInfo, locations, theme } = props;
  const reduxStore = getOrCreateStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const user = getUser(getState());
  const sitesettings = getSiteSettings(getState());
  const { publicRuntimeConfig } = getConfig();
  const [banners, setBannersInLocalState] = useState<TBannersList>(null);
  const organizationType = useOrganizationType();
  const fingerprint = getFingerprint(getState());

  const sravniTMOptions = {
    enabled: isProduction,
    ab: AB_NAME,
    userId: user?.sub,
    googleTmId: 'GTM-PMDFG9',
    theme,
  };

  const fetchRegions = useCallback(() => {
    let centers: ILocation[];

    const centersData = localStorage.getItem('centers');

    if (!centersData) {
      if (locations.length) {
        centers = locations;

        localStorage.setItem('centers', JSON.stringify(centers));
      }
    } else {
      try {
        centers = JSON.parse(centersData);
      } catch (e) {}
    }

    dispatch(setLocations({ centers }));
  }, [dispatch, locations]);

  useEffect(() => {
    if (fingerprint === '-1') {
      FingerprintJS.load()
        .then((fp) => fp.get())
        .then((result) => {
          const id = result.visitorId;

          dispatch(setFingerprint(id));
          JSCookie.set('_FP_', id);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { default: queue } = require('../utils/queue');

    if (isProdBuild) {
      queue.push(() => {
        sentryInit({
          sentryDSN: publicRuntimeConfig.sentryDSN,
          environment: publicRuntimeConfig.environment,
          release: publicRuntimeConfig.release,
          user,
          // ignoreErrors: [],
        });
      });
      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message);
          });
        }
      });
    }

    queue.push(() => fetchRegions());

    queue.push(() => sendSpaIsReadyEvent(deviceInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (banners) return;

    (async () => {
      const result = await getBanners({
        BannerTypes: [BANNER_TYPES.TGBM_7, BANNER_TYPES.OUT_OF_PAGE, BANNER_TYPES.SIDE, BANNER_TYPES.TOP],
        Category: OrganizationTypeToGatewayServiceMap[organizationType],
        Product: 'rating',
        Page: 'calculator',
      });

      setBannersInLocalState(result);
      dispatch(setBanners(result));
    })();
  }, [banners, dispatch, organizationType]);

  return (
    <Interceptor componentName="rating-root-component">
      <StoreProvider store={reduxStore}>
        <ThemeProvider initialTheme={theme}>
          <DeviceInfoProvider initialInfo={deviceInfo}>
            <>
              <CommonHead sravniTMOptions={sravniTMOptions} />
              <Seo />
              <div className={styles['wrapper']}>
                <Banner banner={banners?.top} abName={AB_NAME} type={BANNER_TYPES.TOP} />
                <Header />
                <Component {...pageProps} />
              </div>
              <Footer
                mode="encyclopedia"
                menuLinks={sitesettings.footer?.menu}
                socialLinks={sitesettings.footer?.socialLinks}
                support={sitesettings.footer?.support}
              />
              <Notifications />
            </>
          </DeviceInfoProvider>
        </ThemeProvider>
      </StoreProvider>
    </Interceptor>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  let pageProps = {};
  const reduxStore = getOrCreateStore();
  ctx.store = reduxStore;
  let locations: ILocation[] = [];

  if (typeof window === 'undefined') {
    if (ctx.req.__USER__) {
      reduxStore.dispatch(setUser(formatUserData(ctx.req.__USER__)));
    }
    if (ctx.req.__SITE_SETTINGS__) {
      reduxStore.dispatch(setSettings(ctx.req.__SITE_SETTINGS__));
    }
    if (ctx.req.__SELECTED_LOCATION__) {
      reduxStore.dispatch(setCurrentLocation(ctx.req.__SELECTED_LOCATION__));
    }
    if (ctx.req.__SEO__) {
      reduxStore.dispatch(setMetadata({ seo: ctx.req.__SEO__, pathname: ctx.asPath.split('?') }));
    }
    if (ctx.req.__LOCATIONS__) {
      locations = ctx.req.__LOCATIONS__;
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  let deviceInfo: DeviceInfo = { phone: false, tablet: false, isAppleMobile: false };
  let theme: ThemeNames = ThemeName.lager;

  if (ctx.req && ctx.req.__DEVICE_INFO__) {
    deviceInfo = ctx.req.__DEVICE_INFO__;
  }
  if (ctx.req && ctx.req.__THEME__) {
    theme = ctx.req.__THEME__;
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    realPath: ctx.req?.url || '',
    deviceInfo,
    locations,
    theme,
  };
};

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }

export default MyApp;
